<template>
  <div class="create-payment__additional-fields">
    <div
      class="create-payment__field create-payment__field--select"
      :class="{
        'create-payment__field--error':
          v$.selectedPaymentTypeOption.$errors.length > 0,
      }"
    >
      <div class="create-payment__field-label">
        {{ $t("create_payment.payment_type_option") }}
      </div>
      <div class="create-payment__field-block">
        <v-select
          v-model="selectedPaymentTypeOption"
          :options="paymentTypeOptionsDataSet"
          :placeholder="$t('create_payment.payment_type_option.placeholder')"
        ></v-select>
        <div
          class="create-payment__field-errors"
          v-if="v$.selectedPaymentTypeOption.$errors.length > 0"
        >
          <div class="create-payment__field-error">
            {{ $t(v$.selectedPaymentTypeOption.$errors[0].$message) }}
          </div>
        </div>
      </div>
    </div>
    <div
      class="create-payment__field create-payment__field--select"
      v-if="paymentType.slug === paymentTypesConst.adminGovernmentPayment"
      :class="{
        'create-payment__field--error':
          v$.selectedPaymentTypeOptionTerm.$errors.length > 0,
      }"
    >
      <div class="create-payment__field-label">
        {{ $t("create_payment.payment_type_option_term") }}
      </div>
      <div class="create-payment__field-block">
        <v-select
          v-model="selectedPaymentTypeOptionTerm"
          :options="paymentTypeOptionTermsDataSet"
          :placeholder="
            $t('create_payment.payment_type_option_term.placeholder')
          "
        ></v-select>
        <div
          class="create-payment__field-errors"
          v-if="v$.selectedPaymentTypeOptionTerm.$errors.length > 0"
        >
          <div class="create-payment__field-error">
            {{ $t(v$.selectedPaymentTypeOptionTerm.$errors[0].$message) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ADMIN_GOVERNMENT_PAYMENT } from "@/const/payment-types";
import { PaymentTypeApi } from "@/office/payment/payment-type-api";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";

export default {
  name: "CreatePaymentAdditionalFields",
  model: {
    prop: "value",
    event: "update",
  },
  props: {
    paymentType: {
      type: Object,
      required: true,
    },
    repeatOrderObject: {},
    client: {
      type: Object,
    },
    externalErrors: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      paymentTypeOptions: [],
      paymentTypeOptionTerms: [],
      selectedPaymentTypeOption: null,
      selectedPaymentTypeOptionTerm: null,
      paymentTypesConst: {
        adminGovernmentPayment: ADMIN_GOVERNMENT_PAYMENT,
      },
      additionalFieldsFirstLoad: false,
    };
  },
  validations() {
    let validationsObject = {
      selectedPaymentTypeOption: {
        required: helpers.withMessage("payment_type_option_required", required),
      },
    };

    switch (this.paymentType.slug) {
      case this.paymentTypesConst.adminGovernmentPayment: {
        return {
          ...validationsObject,
          selectedPaymentTypeOptionTerm: {
            required: helpers.withMessage(
              "payment_type_option_term_required",
              required
            ),
          },
        };
      }
    }
  },
  async created() {
    this.loadAdditionalFields().then(() => {
      if (this.repeatOrderObject !== null) {
        this.setRepeatOrderData();
      }
    });
  },
  watch: {
    paymentType: function () {
      if (this.paymentType !== null) {
        this.loadAdditionalFields();
      }
    },
    selectedPaymentTypeOption: function () {
      if (this.selectedPaymentTypeOption === null) {
        this.paymentTypeOptionTerms = [];
      } else {
        this.paymentTypeOptionTerms = this.selectedPaymentTypeOption.terms;
      }

      if (!this.repeatOrderObject) {
        this.selectedPaymentTypeOptionTerm = null;
      }

      this.updateData();
    },
    selectedPaymentTypeOptionTerm: function () {
      this.updateData();
    },
    purpose: function () {
      this.updateData();
    },
  },
  computed: {
    paymentTypeOptionsDataSet() {
      return this.paymentTypeOptions.map((pTO) => ({
        id: pTO.id,
        label: pTO.name,
        terms: pTO.terms,
      }));
    },
    paymentTypeOptionTermsDataSet() {
      return this.paymentTypeOptionTerms.map((pTOT) => ({
        id: pTOT.id,
        label: pTOT.period,
        tax: pTOT.tax,
      }));
    },
    paymentAmount() {
      if (
        this.paymentType.slug === this.paymentTypesConst.adminPayment ||
        this.paymentType.slug === this.paymentTypesConst.adminGovernmentPayment
      ) {
        return this.selectedPaymentTypeOptionTerm
          ? this.selectedPaymentTypeOptionTerm.tax
          : 0;
      }

      return 0;
    },
  },
  methods: {
    async loadAdditionalFields() {
      this.paymentTypeOptions = [];

      if (this.additionalFieldsFirstLoad) {
        this.selectedPaymentTypeOption = null;
      }

      await new PaymentTypeApi()
        .getPaymentTypeFields(this.paymentType.id)
        .then((r) => {
          this.paymentTypeOptions = r.data.options;
          this.additionalFieldsFirstLoad = true;
        });
    },
    updateData() {
      this.$emit("update", {
        payment_type_option_id: this.selectedPaymentTypeOption
          ? this.selectedPaymentTypeOption.id
          : null,
        payment_type_option_term_id: this.selectedPaymentTypeOptionTerm
          ? this.selectedPaymentTypeOptionTerm.id
          : null,
        amount: this.paymentAmount,
      });
    },
    setRepeatOrderData() {
      if (this.repeatOrderObject.paymentData.payment_type_option) {
        this.selectedPaymentTypeOption = {
          id: this.repeatOrderObject.paymentData.payment_type_option.id,
          label: this.repeatOrderObject.paymentData.payment_type_option.name,
          terms: this.repeatOrderObject.paymentData.payment_type_option.terms,
        };
      }

      if (this.repeatOrderObject.paymentData.payment_type_option_term) {
        this.selectedPaymentTypeOptionTerm = {
          id: this.repeatOrderObject.paymentData.payment_type_option_term.id,
          label:
            this.repeatOrderObject.paymentData.payment_type_option_term.period,
          tax: this.repeatOrderObject.paymentData.payment_type_option_term.tax,
        };
      }

      this.$nextTick(() => {
        this.repeatOrderObject = null;
        this.$emit("repeatOrderSet");
      });
    },
    async validate() {
      this.v$.$reset();
      return await this.v$.$validate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";
@import "~@/assets/scss/_mixins.scss";

.create-payment {
  &__additional-fields {
    padding-left: 100px;
    margin-bottom: 24px;
  }

  &__field {
    width: 100%;
    display: flex;
    align-items: center;

    margin-bottom: 25px;

    &:last-child {
      margin-bottom: 0;
    }

    &-label {
      color: $textLight;
      font-size: pxToRem(10);
      line-height: pxToRem(12);
      margin-right: 44px;
      width: 100px;
      word-wrap: break-word;
      flex-shrink: 0;
      flex-grow: 0;
    }
  }
}
</style>
